/* eslint-disable no-console */

import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import {
  getErrorMessage,
  isAxiosError,
  shouldLogErrorInSentry
} from './errorHandling';

const sanitizeApiResponse = (
  response: AxiosError['response'] | undefined
): object | undefined => {
  if (!response) return undefined;

  const config = response.config as Record<string, unknown> | undefined;
  const sanitizedConfig = config
    ? {
        ...config,
        headers: config.headers
          ? {
              ...(config.headers as Record<string, unknown>),
              Authorization: '[REDACTED]'
            }
          : undefined
      }
    : undefined;

  return {
    data: response.data,
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
    config: sanitizedConfig
  };
};

enum ELogLevel {
  Debug = 0,
  Info = 1,
  Warn = 2,
  Error = 3
}

class Logger {
  private static logLevel: ELogLevel = import.meta.env.PROD
    ? ELogLevel.Info
    : ELogLevel.Debug;

  static init(): void {}

  private static shouldLog(level: ELogLevel): boolean {
    return level >= Logger.logLevel;
  }

  static debug(message: string, ...args: any): void {
    if (Logger.shouldLog(ELogLevel.Debug)) {
      console.debug(`[DEBUG]: ${message}`, ...args);
    }
  }

  static info(message: string, ...args: any): void {
    if (Logger.shouldLog(ELogLevel.Info)) {
      console.info(`[INFO]: ${message}`, ...args);
    }
  }

  static warn(message: string, ...args: any): void {
    if (Logger.shouldLog(ELogLevel.Warn)) {
      console.warn(`[WARN]: ${message}`, ...args);
    }
  }

  static error(message: string, ...args: any): void {
    const errorObject: Error | undefined = args.find(
      (arg: any) => arg instanceof Error
    );
    if (Logger.shouldLog(ELogLevel.Error)) {
      if (!errorObject) {
        console.error(`[ERROR]: ${message}`, ...args);
      } else if (shouldLogErrorInSentry(errorObject)) {
        console.error(`[ERROR]: ${message}.`, getErrorMessage(errorObject));
      } else {
        // using warn we avoid sending the error to Sentry, but log it locally
        console.warn(`[ERROR]: ${message}`, ...args);
      }
    }
  }

  static apiError(message: string, error: AxiosError | Error): void {
    if (!Logger.shouldLog(ELogLevel.Error)) return;

    if (!shouldLogErrorInSentry(error)) {
      // Muted error (e.g. Network Error) - log locally without sending to Sentry
      console.warn(`[API ERROR]: ${message}`, error);
      return;
    }

    const axiosError = isAxiosError(error) ? error : undefined;
    const sanitizedResponse = sanitizeApiResponse(axiosError?.response);

    // Use console.warn (not console.error) to avoid double-capture via captureConsoleIntegration,
    // and instead send directly to Sentry with the full sanitized response as extra context.
    console.warn(`[API ERROR]: ${message}`, sanitizedResponse ?? error);
    Sentry.captureException(error, {
      extra: { message, response: sanitizedResponse }
    });
  }

  static getLogLevel(): ELogLevel {
    return Logger.logLevel;
  }
}

export { Logger, ELogLevel };
